import { NameAnd } from "@me8eon/record_utils";
import { makeContextFor } from "@me8eon/context";
import { DebugLog } from "@me8eon/debug";
import { JwtTokenFn, UserData } from "@me8eon/authentication";
import React, { useMemo } from "react";
import { useAccessTokenFn, useJwtToken } from "@me8eon/react_authentication";
import { useFeatureFlag } from "@me8eon/featureflags";
import { PublicClientApplication } from "@azure/msal-browser";
import { useSelectedDataViewName } from "@me8eon/data_view_filter";

//The data views use this to summarise what data sources they need
export type BaseDataSourceDetails = {
    //for example elasticSearch or graphApi
    type: string
    //These are things like 'elastic search indicies'
    names?: string[]
}

export type DataSources<Filter> = NameAnd<DataSource<Filter>>
export type DataSource<Filters> = DataSourceWithGenerics<Filters, any, any, any, any>

export type FiltersPagingAndQuery<Filters, Paging, Query> = {
    filters: Filters
    query: Query
    paging?: Paging
}

export type IndexToAggregates = NameAnd<Aggregates> //the name is the index
export type Aggregates = NameAnd<Aggregate> // the name is the name of the aggregate
export type Aggregate = NameAnd<number>
export type SearchResult<Paging, Results> = RawSearchResult<Paging, Results> & {
    /** the page that we are on. NOTE that this often isn't enough to get the next page. For example when we have a cursor or elastic search's search_after */
    page: number
}

export type RawSearchResult<Paging, Results> = {
    /** Paging data that is needed to 'get the next page' */
    paging?: Paging
    /** Is there any point scrolling again? */
    finished: boolean
    /** The actual results. Note it is often an array of arrays, but might not be*/
    results: Results
    /** Aggregates are things like 'count of status' or 'count of priority'. They are only fetched on the initial search, not when scrolling */
    aggregates: IndexToAggregates

}
export type SearchQuery<Filters, Paging> = {
    filters: Filters
    names: string[]
    paging: Paging | undefined
    debug: DebugLog
    scroll: boolean
    userData: UserData
}
export type SearchQueryFn<Filters, Paging, Query> = (query: SearchQuery<Filters, Paging>) => FiltersPagingAndQuery<Filters, Paging, Query>

/** This is how we get, and just as importantly debug, data
 *
 * The filters contain all the information we need to 'go get the data'. For example the searchText, time,...
 * The Query is 'what do I send to the search'. It can also include debug data.
 * The DevModeData is 'all the stuff I want to see in my dev mode component'
 * The Result is 'what we show on the gui'
 * Paging is used to get the next page of data. It might a cursor, or a page number, or a scroll id or...
 */
export type DataSourceWithGenerics<Filters, Paging, Query, DevModeResult, Results> = {
    plugin: "datasource"
    type: string,
    query: SearchQueryFn<Filters, Paging, Query>
    search(query: Query, debug: DebugLog): Promise<DevModeResult>
    results: (devModeData: DevModeResult, source: string, debug: DebugLog) => RawSearchResult<Paging, Results>
}

export const { use: useDataSources, Provider: RawDataSourcesProvider } = makeContextFor<DataSources<any>, "dataSources">("dataSources");

export type TokenToDataSourcesFn = (dlsTokenFn: JwtTokenFn, graphApiTokenFn: JwtTokenFn, activeFilter: string) => DataSources<any>
type DataSourcesProviderProps = {
    children: React.ReactNode
    dataSources: TokenToDataSourcesFn
    msalInstance: any //a hack until we get rid of the old way of authentication
}

//hack for
function defaultGraphApiTokenFn(
    scopes: string[],
    msalInstance: PublicClientApplication,
): () => Promise<string> {
    return async () => {
        const account = msalInstance.getAllAccounts()[0];
        if (!account) throw new Error("No account found");
        const response = await msalInstance.acquireTokenSilent({ scopes, account });
        return response.accessToken;
    };
}

export function DataSourcesProvider({ children, dataSources, msalInstance }: DataSourcesProviderProps) {
    const tokenFn = useJwtToken();
    const newGraphApiFn = useAccessTokenFn("graphApi");
    const selectedOps = useSelectedDataViewName();
    const activeFilter = selectedOps[0];
    const ds = useMemo(() => {
        const graphApiTokenFn: JwtTokenFn = newGraphApiFn;
        return dataSources(tokenFn, graphApiTokenFn, activeFilter);
    }, [dataSources, tokenFn, activeFilter]);
    return <RawDataSourcesProvider dataSources={ds} >{children}</RawDataSourcesProvider>;
}